import { getFirebase } from '@assets/firebase/FirebaseAdapater';
import {
    getStorageAdapater,
    StorageTarget
} from '@assets/firebase/StorageAdapater';
import { ServiceFileEntry, useStore } from '@assets/model/Store';
import { observer } from 'mobx-react';
import { StorageUpload } from '../StorageUpload/StorageUpload';
import * as style from './ProjectFiles.module.less';

import classNames from 'classnames';
import { AnimationItem } from 'lottie-web';
import { AnimatePresence, motion, usePresence } from 'motion/react';
import { MouseEvent, useEffect, useState } from 'react';
import { LottieAnim } from '../LottieAnim/LottieAnim';
import { Flex } from '../UI/Flex/Flex';
import animData from './LoadingAnim.json';
import { cleanFilenameForDisplay } from '@assets/utils/Utils';
import path from 'path';
import icon_trash from './icons/trash.svg';
import icon_plus from './icons/plus.svg';

interface IProjectFilesProps {
    requestSeeFile: (fileUrl: string, displayName: string) => void;
    selectedFileKey?: string;
}

const getIsFileReady = (doc: ServiceFileEntry) => {
    if (doc.status == 'error') {
        return true;
    }
    if (doc.status == 'completed') {
        // if (doc?.ragStatus == 'completed') {
        //     return true;
        // }
        return true;
    }
    return false;
};

const ProcessingAnimation = () => {
    const [isPresent, safeToRemove] = usePresence();
    const [didFinishAnimation, setDidFinishAnimation] = useState(false);
    const isAnimOut = !isPresent;

    const onEnterFrame = (lottie: AnimationItem) => {
        const frame = lottie.currentFrame;
        if (isPresent && frame > 84) {
            lottie.goToAndPlay(0, true);
        } else {
            if (frame > 200) {
                lottie.goToAndStop(200, true);
                setTimeout(() => {
                    setDidFinishAnimation(true);
                }, 500);
            }
        }
    };

    useEffect(() => {
        if (!isPresent && didFinishAnimation) safeToRemove();
    }, [isPresent, didFinishAnimation]);

    return (
        <LottieAnim
            width={50}
            height={50}
            data={animData}
            onFrame={onEnterFrame}
        />
    );
};

const ProjectFileButtons = (props: {
    onView: () => void;
    onDelete?: () => void;
    deleteEnabled: boolean;
    isSelected: boolean;
}) => {
    // The tile itself is clickable, so the buttons must not double-fire it.
    const stop = (e: MouseEvent, action: () => void) => {
        e.stopPropagation();
        action();
    };

    return (
        <div className={style.tile_actions}>
            <button
                className={classNames(
                    style.btn,
                    props.isSelected && style.btn_active
                )}
                onClick={e => stop(e, props.onView)}
            >
                View
            </button>
            <button
                className={classNames(style.btn, style.btn_icon_only)}
                disabled={!props.deleteEnabled}
                title="Delete"
                onClick={e => stop(e, props.onDelete)}
            >
                <img src={icon_trash} alt="Delete" />
            </button>
        </div>
    );
};

//Kinda hacky - better to not have all these tricks with the ID matching
export function sanitizeName(filepath: string): string {
    if (!filepath) {
        return '';
    }

    // path.parse().name extracts the file name without the extension (equivalent to p.stem)
    const stem = path.parse(filepath).name;

    // Remove the "_unlocked" suffix if present
    const cleanedStem = stem.replace('_unlocked', '');

    // Filter out any characters that are NOT alphanumeric, -, _, (, or )
    // [^a-zA-Z0-9\-_()] matches any character NOT inside the brackets and replaces it with ""
    return cleanedStem.replace(/[^a-zA-Z0-9\-_()]/g, '');
}
export const ProjectFiles = observer((props: IProjectFilesProps) => {
    const [loadedOnMount, setLoadedOnMount] = useState<{
        [key: string]: boolean;
    }>(null);
    const [selectedKey, setSelectedKey] = useState<string>(null);

    const store = useStore();
    const projectFilesRaw = store.projectFiles?.files || {};

    const projectFileKeys = Object.keys(projectFilesRaw);

    if (loadedOnMount == null) {
        var loaded = {};
        projectFileKeys.forEach(key => {
            const file = projectFilesRaw[key];
            loaded[key] = getIsFileReady(file);
        });

        setLoadedOnMount(loaded);
    }

    const query = store.query;
    const reportId = store.selectedReportId;
    const companyName = store.query.companyName;

    const targetUploadStorage: StorageTarget = {
        type: 'rag',
        reportId: reportId
    };

    function seeProjectFile(fileKey: string) {
        const filename = sanitizeName(query.value('pdf'));
        if (fileKey === filename) {
            props.requestSeeFile(query.value('pdfViewUrl'), fileKey);
        } else {
            const fileUrl = projectFilesRaw[fileKey].url;
            props.requestSeeFile(fileUrl, fileKey);
        }
    }

    function deleteFile(fileKey: string) {
        const areYouSure = window.confirm(
            'Are you sure you want to delete this file? ' + fileKey
        );

        async function deleteFile() {
            const storage = getStorageAdapater();
            const deleteFileName = projectFilesRaw[fileKey].filename;
            storage.deleteFileFromStorage(targetUploadStorage, deleteFileName);

            const fb = getFirebase();
            fb.deleteProjectFile(reportId, fileKey);
            fb.triggerScanRagFiles();
        }
        if (areYouSure) {
            deleteFile();
        }
    }

    // Overview text can arrive as a string or as a list of bullet lines.
    const overviewText = (overview: any) => {
        if (Array.isArray(overview)) {
            return overview.join(' ');
        }
        return overview ?? '';
    };

    const Tile = (props: {
        fileKey: string;
        title: string;
        subtitle: string;
        overview: string;
        isReady: boolean;
        deleteEnabled: boolean;
        onView: () => void;
        onDelete: () => void;
    }) => {
        const isSelected = selectedKey === props.fileKey;
        const onOpen = () => {
            setSelectedKey(props.fileKey);
            props.onView();
        };

        return (
            <div
                className={classNames(
                    style.tile,
                    isSelected && style.tile_selected
                )}
                onClick={onOpen}
            >
                <div className={style.tile_title_row}>
                    <div className={style.tile_title} title={props.title}>
                        {props.title}
                    </div>
                    <AnimatePresence mode="popLayout">
                        {props.isReady ? (
                            <motion.div
                                key={props.fileKey}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{
                                    duration: 0.3,
                                    delay: loadedOnMount[props.fileKey] ? 0 : 5
                                }}
                            >
                                <ProjectFileButtons
                                    onView={onOpen}
                                    onDelete={props.onDelete}
                                    deleteEnabled={props.deleteEnabled}
                                    isSelected={isSelected}
                                />
                            </motion.div>
                        ) : (
                            <motion.div
                                key={props.fileKey + '_animation'}
                                className={style.processing_cell}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{
                                    opacity: 0,
                                    transition: { duration: 0.3, delay: 4.5 }
                                }}
                            >
                                <ProcessingAnimation />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
                {props.subtitle && (
                    <div className={style.tile_date}>{props.subtitle}</div>
                )}
                <div className={style.tile_overview}>{props.overview}</div>
            </div>
        );
    };

    projectFileKeys.sort((a, b) => {
        if (a === store.selectedReportId) return -1;
        if (b === store.selectedReportId) return 1;
        var bDate = new Date(projectFilesRaw[b]?.summary?.created_at ?? 0);
        var aDate = new Date(projectFilesRaw[a]?.summary?.created_at ?? 0);

        return bDate.getTime() - aDate.getTime();
    });

    const projectFiles = projectFileKeys.map(fileKey => {
        const file = projectFilesRaw[fileKey];
        const display =
            cleanFilenameForDisplay(file?.filename) ?? 'Unknown File';
        const overview =
            file?.status === 'error'
                ? `Failed to process file: ${file?.errorReason}`
                : overviewText(file?.summary?.doc_overview);

        return (
            <Tile
                key={fileKey}
                fileKey={fileKey}
                title={display}
                subtitle={file?.summary?.created_at ?? ''}
                overview={overview}
                isReady={getIsFileReady(file)}
                deleteEnabled={
                    file?.saveKey !== sanitizeName(query.value('pdf')) &&
                    file?.saveKey !== `${reportId}_Flightline`
                }
                onView={() => seeProjectFile(file.saveKey)}
                onDelete={() => {
                    deleteFile(file.saveKey);
                }}
            />
        );
    });

    const allFiles = projectFiles;
    return (
        <Flex
            direction="column"
            className={style.round_border_container}
            fillCell={true}
        >
            <Flex
                className={style.card_header}
                direction="row"
                alignItems="center"
            >
                <div className={style.card_header_title}>Case Files</div>
                <StorageUpload
                    className={style.uploadButton}
                    display={
                        <>
                            <img src={icon_plus} alt="" />
                            Add
                        </>
                    }
                    toasterMessage={`${companyName}: %name%`}
                    storageTarget={targetUploadStorage}
                    allowedExtensions={['.pdf', '.md']}
                    onUpload={fileName => {
                        const fb = getFirebase();
                        fb.addDummyProjectFile(reportId, fileName);
                        fb.triggerScanRagFiles();
                    }}
                />
            </Flex>
            <div className={style.tiles_wrap}>
                {allFiles.length === 0 && (
                    <div className={style.treeEmptyText}>No case files yet</div>
                )}
                <AnimatePresence>
                    {allFiles.map((file, index) => {
                        return (
                            <motion.div
                                key={file.key}
                                className={style.tile_group}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -10 }}
                                transition={{
                                    duration: 0.3,
                                    delay: index * 0.1
                                }}
                            >
                                {file}
                                {index < allFiles.length - 1 && (
                                    <div className={style.tile_divider} />
                                )}
                            </motion.div>
                        );
                    })}
                </AnimatePresence>
            </div>
        </Flex>
    );
});
